import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { PawPrint, Heart, Bone } from "./Doodles";

const GOFUNDME_URL = "https://www.gofundme.com/f/support-project-archie-doghouses-for-shelters";

const MATERIALS = [
  { icon: "timber", text: "Timber & plywood for walls, floors, and roofs" },
  { icon: "screws", text: "Screws, hinges, and hardware that hold it all together" },
  { icon: "paint", text: "Weatherproof paint so every doghouse survives the seasons" },
  { icon: "tools", text: "Drill bits, sandpaper, and tool wear from every build day" },
];

const Donate = () => (
  <section
    id="donate"
    data-testid="donate-section"
    className="relative scroll-mt-24 overflow-hidden border-b-2 border-[#16294B] bg-[#16294B] py-24 md:py-32"
    aria-label="Donate to Project Archie"
  >
    <span className="pointer-events-none absolute left-8 top-10 hidden opacity-20 md:block" aria-hidden="true">
      <PawPrint className="h-24 w-24 -rotate-12" color="#FBF3E3" />
    </span>
    <span className="pointer-events-none absolute bottom-10 right-8 hidden opacity-20 md:block" aria-hidden="true">
      <Bone className="h-20 w-20 rotate-12" color="#FBF3E3" />
    </span>

    <div className="relative z-10 mx-auto max-w-4xl px-5 text-center sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
      >
        <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#FBF3E3] px-5 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-[#FBF3E3]">
          <Heart className="h-4 w-4" /> Support the builds
        </span>
        <h2 className="mt-6 font-display text-5xl leading-tight text-[#FBF3E3] md:text-7xl">
          Help us fund the <span className="text-[#E63946]">next doghouse</span>
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#FBF3E3]/85 md:text-lg">
          Every build starts as a pile of raw materials — and materials cost money. Timber,
          plywood, screws, paint, and tool supplies all add up before a single wall goes up.
          Any contribution, big or small, eases the project and turns directly into a warm,
          safe home for a shelter dog in San Diego.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="mx-auto mt-12 grid max-w-3xl gap-4 text-left sm:grid-cols-2"
      >
        {MATERIALS.map((m, i) => (
          <motion.div
            key={m.icon}
            data-testid={`donate-material-${i}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
            className="flex items-start gap-3 rounded-xl border-2 border-[#FBF3E3]/30 bg-[#FBF3E3]/5 p-4"
          >
            <PawPrint className="mt-0.5 h-6 w-6 shrink-0" color={i % 2 === 0 ? "#3B82F6" : "#E63946"} />
            <span className="text-sm font-medium leading-relaxed text-[#FBF3E3] md:text-base">{m.text}</span>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-12"
      >
        <a
          data-testid="donate-gofundme-btn"
          href={GOFUNDME_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-press inline-flex items-center gap-3 rounded-full border-2 border-[#FBF3E3] bg-[#E63946] px-10 py-4 text-sm font-bold uppercase tracking-widest text-[#FBF3E3] shadow-[6px_6px_0_0_#FBF3E3]"
        >
          Donate on GoFundMe <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </a>
        <p className="mt-5 text-xs text-[#FBF3E3]/60">
          Every dollar goes straight to build materials. No overhead, no fees taken by us.
        </p>
      </motion.div>
    </div>
  </section>
);

export default Donate;
